import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, IconButton, Tooltip, Typography, Alert, Button, CircularProgress, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddCommentIcon from '@mui/icons-material/AddComment';
import BookmarkAddIcon from '@mui/icons-material/BookmarkAdd';
import CollectionsBookmarkIcon from '@mui/icons-material/CollectionsBookmark';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import SettingsIcon from '@mui/icons-material/Settings';
import { ChatSessionContext } from './ChatSessionProvider';
import { ReservoirChatContext } from './ReservoirChatContext';
import { UserContext } from '../App';
import { useToast } from '../usalco_lib/ToastContext';
import { saveResultInsight, saveConversationInsight } from './insightsApi';
import { exportConversationToEml } from './emailExport';
import { getProfiles } from './catalystApi';
import { AI_INSIGHTS_SECURITY_GUID, EXPERIMENTAL_FEATURES_SECURITY_GUID } from './constants';
import ChatMessage from './ChatMessage';
import ChatInput from './ChatInput';
import ChatSettingsOverlay from './ChatSettingsOverlay';
import CatalystLogo from '../images/catalyst.png';
import styles from './ChatPanel.module.css';

// The user text of the nearest user turn before assistant message index `i`.
function questionBefore(messages, i) {
  for (let j = i - 1; j >= 0; j -= 1) {
    if (messages[j].role === 'user') {
      return messages[j].parts.filter((p) => p.type === 'text').map((p) => p.text).join('');
    }
  }
  return '';
}

export default function ChatPanel() {
  const chat = React.useContext(ChatSessionContext);
  const { context, reloadReport } = React.useContext(ReservoirChatContext);
  const userContext = React.useContext(UserContext);
  const hasInsightsAccess = Array.isArray(userContext?.securities)
    && userContext.securities.some((s) => s.ua_fid === AI_INSIGHTS_SECURITY_GUID);
  const hasExperimentalAccess = Array.isArray(userContext?.securities)
    && userContext.securities.some((s) => s.ua_fid === EXPERIMENTAL_FEATURES_SECURITY_GUID);
  const showToast = useToast();
  const navigate = useNavigate();
  const scrollRef = React.useRef(null);
  const inputRef = React.useRef(null);

  React.useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [chat.messages, chat.status]);

  React.useEffect(() => {
    if (chat.open) inputRef.current?.focus();
  }, [chat.open]);

  // Skills attached (in Catalyst admin) to this session's active profile. Read-only
  // indicator — management lives entirely in the Catalyst admin console. Fetched once;
  // the active profile switches between report-view and builder within a session.
  const [profiles, setProfiles] = React.useState(null);
  React.useEffect(() => {
    let cancelled = false;
    getProfiles()
      .then((data) => { if (!cancelled) setProfiles(data); })
      .catch(() => { if (!cancelled) setProfiles([]); });
    return () => { cancelled = true; };
  }, []);
  const activeProfile = React.useMemo(() => {
    if (!profiles || !chat.activeProfileId) return null;
    return profiles.find((x) => x.id === chat.activeProfileId) || null;
  }, [profiles, chat.activeProfileId]);
  const activeSkills = activeProfile?.skills || [];
  const activeDisplayStyle = activeProfile?.displayStyle || null;
  const activeDisplayStyleName = activeProfile?.displayStyleName || null;
  const activeProfileName = activeProfile?.name || null;

  const [settingsOpen, setSettingsOpen] = React.useState(false);
  const [emailExporting, setEmailExporting] = React.useState(false);

  const provenance = () => ({ reportGuid: context?.reportID || null, chatId: chat.chatId || null });

  const handleSaveResult = React.useCallback((part, question) => {
    saveResultInsight({
      title: question || part.toolName,
      question: question || null,
      payload: { toolName: part.toolName, result: part.result },
      ...provenance(),
    })
      .then(() => showToast('Saved to your insights.', 'success'))
      .catch((err) => showToast(err.response?.data?.error || 'Failed to save insight.', 'error'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context, chat.chatId, showToast]);

  const handleSaveConversation = React.useCallback(() => {
    const firstUser = chat.messages.find((m) => m.role === 'user');
    const title = firstUser
      ? firstUser.parts.filter((p) => p.type === 'text').map((p) => p.text).join('')
      : 'Saved conversation';
    saveConversationInsight({ title, messages: chat.messages, ...provenance() })
      .then(() => showToast('Conversation saved to your insights.', 'success'))
      .catch((err) => showToast(err.response?.data?.error || 'Failed to save conversation.', 'error'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context, chat.chatId, chat.messages, showToast]);

  // Build an .eml of the whole conversation and download it. The user opens the file to get
  // an editable email draft (charts ride as inline cid: images so they render in Outlook).
  const handleExportEmail = React.useCallback(() => {
    if (!chat.messages.length) return;
    const firstUser = chat.messages.find((m) => m.role === 'user');
    const subject = firstUser
      ? firstUser.parts.filter((p) => p.type === 'text').map((p) => p.text).join('')
      : 'Catalyst conversation';
    setEmailExporting(true);
    exportConversationToEml(chat.messages, { subject })
      .then(() => showToast('Email draft (.eml) downloaded — open it to edit and send.', 'success'))
      .catch((err) => showToast(err?.message || 'Failed to export email.', 'error'))
      .finally(() => setEmailExporting(false));
  }, [chat.messages, showToast]);

  // Reload after the pinned cache expired: rebuild the report's pins (they flow back into
  // context.datasetCaches so the next question hits the fresh snapshot) and start a clean chat turn.
  // Fall back to a full page reload if no report registered a reload callback.
  const handleCacheExpiredReload = React.useCallback(() => {
    chat.clearCacheExpired?.();
    if (typeof reloadReport === 'function') {
      reloadReport();
      chat.reset?.();
    } else {
      window.location.reload();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadReport, chat.clearCacheExpired, chat.reset]);

  const hasMessages = chat.messages.length > 0;

  // Gate the chat until the report has resolved at least one dataset cache. Without a pin the
  // MCP falls back to "newest cache", which may differ from what the report will ultimately show.
  // Builder and standalone contexts don't use datasetCaches, so they are always ready.
  const reportKinds = ['report4', 'report2', 'report3'];
  const reportIsReady = !reportKinds.includes(context?.kind)
    || !context.datasetCaches
    || Object.keys(context.datasetCaches).length > 0;

  return (
    <Box className={styles.panelRoot} sx={{ display: 'flex', flexDirection: 'column', height: '100%', minWidth: 0 }}>

      {/* Gradient header */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        pl: 0.5,
        pr: 2,
        py: 0.5,
        background: 'linear-gradient(135deg, #262a56 0%, #5155d5 100%)',
        flexShrink: 0,
      }}>
        <img src={CatalystLogo} alt="Catalyst" style={{ height: 36, width: 'auto' }} />
        <Typography variant="subtitle1" sx={{ flexGrow: 1, color: 'white', fontWeight: 600, fontSize: 14 }}>
          Catalyst Data Assistant
        </Typography>
        {hasExperimentalAccess && (
          <Tooltip title="Export to email (.eml)">
            <span>
              <IconButton
                size="small"
                onClick={handleExportEmail}
                disabled={!hasMessages || chat.status === 'streaming' || emailExporting}
                aria-label="Export to email"
                sx={{ color: 'rgba(255,255,255,0.8)', '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.12)' } }}
              >
                {emailExporting
                  ? <CircularProgress size={16} sx={{ color: 'rgba(255,255,255,0.8)' }} />
                  : <EmailOutlinedIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
        )}
        {hasInsightsAccess && (
          <Tooltip title="Save conversation">
            <span>
              <IconButton
                size="small"
                onClick={handleSaveConversation}
                disabled={!hasMessages || chat.status === 'streaming'}
                aria-label="Save conversation"
                sx={{ color: 'rgba(255,255,255,0.8)', '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.12)' } }}
              >
                <BookmarkAddIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
        {hasInsightsAccess && (
          <Tooltip title="Saved insights">
            <IconButton
              size="small"
              onClick={() => navigate('/insights')}
              aria-label="Saved insights"
              sx={{ color: 'rgba(255,255,255,0.8)', '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.12)' } }}
            >
              <CollectionsBookmarkIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="Chat settings">
          <IconButton
            size="small"
            onClick={() => setSettingsOpen((v) => !v)}
            aria-label="Chat settings"
            sx={{
              color: settingsOpen ? '#fff' : 'rgba(255,255,255,0.8)',
              bgcolor: settingsOpen ? 'rgba(255,255,255,0.15)' : undefined,
              '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.12)' },
            }}
          >
            <SettingsIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="New chat">
          <span>
            <IconButton
              size="small"
              onClick={chat.reset}
              disabled={chat.status === 'streaming'}
              aria-label="New chat"
              sx={{ color: 'rgba(255,255,255,0.8)', '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.12)' } }}
            >
              <AddCommentIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Close">
          <IconButton
            size="small"
            onClick={() => chat.setOpen(false)}
            aria-label="Close chat"
            sx={{ color: 'rgba(255,255,255,0.8)', '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.12)' } }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Content area — the settings overlay covers this when open */}
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', position: 'relative', overflow: 'hidden' }}>
        {/* Message list */}
        <Box ref={scrollRef} sx={{ flexGrow: 1, overflowY: 'auto', overflowX: 'hidden', px: 1.5, py: 1 }}>
          {chat.messages.length === 0 ? (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Ask about the data on this page, or request a chart or table.
              </Typography>
              <Typography variant="body2" color="text.disabled" sx={{ mt: 1 }}>
                Catalyst uses AI and can make mistakes. Please verify important responses.
              </Typography>
            </Box>
          ) : (
            chat.messages.map((m, i) => (
              <ChatMessage
                key={i}
                message={m}
                isStreaming={chat.status === 'streaming' && i === chat.messages.length - 1}
                onSaveResult={hasInsightsAccess ? (part) => handleSaveResult(part, questionBefore(chat.messages, i)) : null}
              />
            ))
          )}
          {chat.status === 'streaming' ? (
            <div className={styles.streamingDots}>
              <span className={styles.streamingDot} />
              <span className={styles.streamingDot} />
              <span className={styles.streamingDot} />
            </div>
          ) : null}
          {chat.error ? <Alert severity="error" sx={{ mt: 1 }}>{chat.error}</Alert> : null}
        </Box>

        {!reportIsReady && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ px: 1.5, py: 0.75, bgcolor: 'rgba(81,85,213,0.05)', borderTop: '1px solid', borderColor: 'divider', flexShrink: 0, display: 'block' }}
          >
            Waiting for report data to load…
          </Typography>
        )}
        <ChatInput onSend={chat.sendMessage} disabled={chat.status === 'streaming' || !reportIsReady} inputRef={inputRef} />

        {settingsOpen && (
          <ChatSettingsOverlay
            onClose={() => setSettingsOpen(false)}
            context={context}
            skills={activeSkills}
            displayStyle={activeDisplayStyle}
            displayStyleName={activeDisplayStyleName}
            profileName={activeProfileName}
          />
        )}
      </Box>

      <Dialog
        open={!!chat.cacheExpired}
        onClose={() => chat.clearCacheExpired?.()}
        aria-labelledby="chat-cache-expired-title"
        aria-describedby="chat-cache-expired-description"
      >
        <DialogTitle id="chat-cache-expired-title">Report data expired</DialogTitle>
        <DialogContent>
          <DialogContentText id="chat-cache-expired-description">
            This report has been open for a while and its data has expired. Reload to see the latest data.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => chat.clearCacheExpired?.()}>Close</Button>
          <Button variant="contained" onClick={handleCacheExpiredReload} autoFocus>Reload</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
